import { useSyncExternalStore } from "react";

// HEIC, the format iPhones take photos in: Tripmo keeps it, but only some browsers can draw it (Safari
// can; Chrome and Firefox can't). An own photo in HEIC shows as a photo where this browser draws it,
// and as a file to download where it can't (features/photos.md, Gotchas). Added on the web, a HEIC
// photo is turned into a JPEG on the device first, where the browser can read it.

// A 16×16 HEIC (made with macOS's sips), to ask this browser whether it draws HEIC at all.
const PROBE =
  "data:image/heic;base64,AAAAJGZ0eXBoZWljAAAAAG1pZjFNaVBybWlhZk1pSEJoZWljAAABhm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAHBpY3QAAAAAAAAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAAADnBpdG0AAAAAAAEAAAAjaWluZgAAAAAAAQAAABVpbmZlAgAAAAABAABodmMxAAAAAOZpcHJwAAAAxWlwY28AAAATY29scm5jbHgAAgACAAaAAAAADGNsbGkAywBAAAAAFGlzcGUAAAAAAAAAEAAAABAAAAAJaXJvdAAAAAAQcGl4aQAAAAADCAgIAAAAcWh2Y0MBA3AAAACwAAAAAAAe8AD8/fj4AAALA6AAAQAXQAEMAf//A3AAAAMAsAAAAwAAAwAecCShAAEAI0IBAQNwAAADALAAAAMAAAMAHqAUIEHAkwziHuRZVNwICBgCogABAAlEAcBhcshAUyQAAAAZaXBtYQAAAAAAAAABAAEGgQIDBYaEAAAAHmlsb2MAAAAARAAAAQABAAAAAQAAAboAAAA7AAAAAW1kYXQAAAAAAAAASwAAADcoAa+jVSq0//zhP/9DesvIUpX/1TL/9/p//mXtB/9J17649/mbbs7/v++mmhLTUcIzxQurUu7g";

let drawsHeic: boolean | null = null;
const listeners = new Set<() => void>();

// Asked once, when the app starts; the answer comes in a moment.
function probe() {
  if (typeof Image === "undefined" || drawsHeic !== null) return;
  const img = new Image();
  const settle = (yes: boolean) => {
    drawsHeic = yes;
    for (const l of listeners) l();
  };
  img.onload = () => settle(img.naturalWidth > 0);
  img.onerror = () => settle(false);
  img.src = PROBE;
}
probe();

// Whether this browser draws HEIC: null until it's known (a moment after the app starts).
export function useDrawsHeic(): boolean | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => drawsHeic,
  );
}

export const isHeic = (f: { type: string; name: string }) =>
  /^image\/hei[cf](-sequence)?$/i.test(f.type) || /\.hei[cf]$/i.test(f.name);

// The most pixels a photo is turned into: about 12 megapixels (an iPhone's usual photo), within what
// any phone's canvas draws (iPhones stop at about 16).
const MAX_PIXELS = 12_200_000;

// A HEIC photo as a JPEG, made here where the browser reads HEIC (Safari); null where it can't, and
// the photo goes as it is. Drawn the way it's meant to be seen (the browser turns it upright), and
// without its other metadata (where it was taken).
export async function heicToJpeg(file: File): Promise<File | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return null;
    const scale = Math.min(1, Math.sqrt(MAX_PIXELS / (w * h)));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const g = canvas.getContext("2d");
    if (!g) return null;
    g.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    if (!blob) return null;
    return new File([blob], `${file.name.replace(/\.hei[cf]$/i, "")}.jpg`, { type: "image/jpeg" });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
